<template>
  <div class="d-flex flex-column" v-if="featureStore.featureOrderManagment">
    <HeaderNavbar
      :title="pageTitle"
      :breadcrumbs="listBreadcrumbs"
      title-size="sm"
      :no-bottom-margin="true"
      :no-border="currentStep.key === 'self_service'">
      <template #subtitle v-if="currentStep.key === 'self_service'">
        <div class="tw:mt-1 tw:flex tw:items-center tw:gap-3">
          <span class="tw:text-14 tw:text-gray-500">{{ order?.provider_job_id }}</span>
          <LeadStatusPill v-if="order?.lead_order_status" :status="order.lead_order_status" />
        </div>
      </template>
    </HeaderNavbar>
    <!-- per-step padding lives on each step's root (.step-inner-padded in lead.scss) -->
    <div class="container-lead d-flex flex-column pin-to-viewport no-padding">
      <Transition name="wizard-area-fade" mode="out-in">
        <OrderCreationLoader
          v-if="isCampaignGenerationLoading"
          key="campaign-loader"
          data-testid="oc-campaign-generation-loader"
          :messages="campaignLoaderMessages"
          :providers="campaignLoaderStages" />
        <div v-else key="step-area" class="position-relative d-flex flex-column flex-grow-1">
          <div v-if="isLoading || (currentStep.section?.shouldBeAwaited && !isReady)" class="step-loader-overlay">
            <OrderCreationLoader
              :title="$t('order_creation.loader.content.title')"
              :subtitle="$t('order_creation.loader.content.subtitle')" />
          </div>
          <div class="d-flex flex-column flex-grow-1">
            <div
              class="step-content-fade d-flex flex-column flex-grow-1"
              :class="{ 'step-content-hidden': isLoading || (currentStep.section?.shouldBeAwaited && !isReady) }">
              <Transition name="fade" mode="out-in">
                <InitialStep v-if="currentStep.key === 'source'" v-model:allowNext="availableSteps.source.canGoNext" />
                <ImportStep
                  v-else-if="currentStep.key === 'import'"
                  v-model:allowNext="availableSteps.import.canGoNext" />
                <DetailsStep
                  v-else-if="currentStep.key?.startsWith('details')"
                  v-model:allowNext="availableSteps.details.canGoNext"
                  @ready="isReady = true" />
                <MediaStep
                  v-else-if="currentStep.key === 'media'"
                  ref="mediaStepRef"
                  v-model:allowNext="availableSteps.media.canGoNext" />
                <PublicationTypeStep
                  v-else-if="currentStep.key === 'publication_type'"
                  v-model:allowNext="availableSteps.publication_type.canGoNext" />
                <PublicationTemplateStep
                  v-else-if="currentStep.key === 'publication_template'"
                  ref="publicationTemplateStepRef"
                  v-model:allowNext="availableSteps.publication_template.canGoNext" />
                <PublicationEditorStep
                  v-else-if="currentStep.key === 'publication_editor'"
                  ref="publicationEditorStepRef"
                  v-model:allowNext="availableSteps.publication_editor.canGoNext" />
                <ProductsStep
                  v-else-if="currentStep.key === 'products'"
                  v-model:allowNext="availableSteps.products.canGoNext" />
                <SelfServiceStep
                  v-else-if="currentStep.key === 'self_service'"
                  ref="selfServiceStepRef"
                  v-model:allowNext="availableSteps.self_service.canGoNext" />
                <FinalReviewStep
                  v-else-if="currentStep.key === 'final_review'"
                  v-model:allowNext="availableSteps.final_review.canGoNext" />
              </Transition>
            </div>
          </div>
        </div>
      </Transition>
      <FixedFooterSection v-if="!['final_review'].includes(currentStep.key) && !isCampaignGenerationLoading">
        <div class="hidden-placeholder" aria-hidden="true"></div>
        <div class="d-flex align-items-center gap-3" data-testid="orders-wizard-step-buttons">
          <div class="d-flex justify-content-end align-items-center gap-3" v-if="currentStep.key === 'products'">
            <button
              type="button"
              class="btn btn-icon p-2 rounded-circle position-relative"
              data-testid="orders-wizard-cart-toggle-button"
              @click="openCart = true">
              <IconShoppingCart />
              <span
                class="position-absolute top-0 start-100 translate-middle badge bg-primary rounded-circle"
                v-if="productsToSave.length">
                {{ productsToSave.length }}
                <span class="visually-hidden">unread messages</span>
              </span>
            </button>
            <p class="m-0 fw-semibold cursor-pointer" @click="openCart = true">Products in Cart</p>
            <div class="vr"></div>
          </div>
          <Transition name="fade">
            <div
              v-if="showSelfServiceBlockedHint"
              class="d-flex align-items-center gap-2 self-service-blocked-hint"
              data-testid="orders-wizard-self-service-blocked-hint">
              <IconInfoCircle :size="18" class="flex-shrink-0" />
              <p class="m-0">{{ selfServiceBlockedMessage }}</p>
            </div>
          </Transition>
          <div class="btn-list justify-content-end">
            <PublicationStateIndicator v-if="currentStep.key === 'publication_editor'" />
            <Transition name="fade">
              <button
                data-testid="orders-wizard-step-back-button"
                @click.prevent="currentStepSequence--"
                class="btn btn-white"
                v-if="currentStepSequence > orderDetailsStep.sequence && currentStep.section?.canGoBack !== false">
                <IconArrowLeft class="ms-1" :size="20" /> {{ $t('btn.back') }}
              </button>
            </Transition>
            <button
              v-if="currentStep.key === 'self_service' && selfServiceStepRef?.hasNextProvider"
              data-testid="orders-wizard-step-self-service-next-button"
              class="btn btn-primary"
              :disabled="!selfServiceStepRef?.currentProviderValid || isLoading"
              @click.prevent="handleNextStep">
              {{ $t('btn.save_and_continue') }}
              <IconArrowRight class="ms-1" :size="20" />
            </button>
            <button
              v-else
              data-testid="orders-wizard-step-next-button"
              class="btn btn-primary"
              @click.prevent="handleNextStep"
              :disabled="
                (currentStep.key === 'self_service' ? !availableSteps.self_service.canGoNext : !canClickNext) ||
                isLoading ||
                isPublicationLoading
              ">
              {{ $t('btn.continue') }}
              <IconArrowRight class="ms-1" :size="20" />
            </button>
          </div>
        </div>
      </FixedFooterSection>
    </div>
  </div>
  <div v-else>
    <HeaderNavbar :title="pageTitle" :breadcrumbs="listBreadcrumbs" :no-bottom-margin="true" />
    <div class="empty">
      <div class="empty-header">{{ $t('orders.create.empty.header') }}</div>
      <p class="empty-title">{{ $t('orders.create.empty.title') }}</p>
      <p class="empty-subtitle text-secondary">
        {{ $t('orders.create.empty.subtitle') }}
      </p>
    </div>
  </div>
</template>
<script lang="ts" setup>
import translator from '@/locale/translator';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { resolveCampaignTaskState, useOrderCreationStore } from '@/stores/orderCreation';
import { computed, onMounted, onBeforeUnmount, watch, ref, nextTick } from 'vue';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import { storeToRefs } from 'pinia';
import FixedFooterSection from '@/components/generic/FixedFooterSection.vue';
import { IconArrowLeft, IconArrowRight, IconInfoCircle, IconPhoto, IconShoppingCart } from '@tabler/icons-vue';
import LeadStatusPill from '@/components/lead-design/LeadStatusPill.vue';

import OrderCreationLoader from '@/components/orders/creation/subcomponents/OrderCreationLoader.vue';
import { getCampaignProviderMeta } from '@/components/orders/creation/subcomponents/campaignProviderIcons';
import InitialStep from '@/components/orders/creation/OrderCreationInitialStep.vue';
import ImportStep from '@/components/orders/creation/OrderCreationImportStep.vue';
import DetailsStep from '@/components/orders/creation/OrderCreationDetailsStep.vue';
import MediaStep from '@/components/orders/creation/OrderCreationMediaStep.vue';
import ProductsStep from '@/components/orders/creation/OrderCreationProductsStep.vue';
import { useRoute, useRouter } from 'vue-router';
import PublicationTypeStep from '@/components/orders/creation/OrderCreationPublicationTypeStep.vue';
import PublicationTemplateStep from '@/components/orders/creation/OrderCreationPublicationTemplateStep.vue';
import PublicationEditorStep from '@/components/orders/creation/OrderCreationPublicationEditorStep.vue';
import SelfServiceStep from '@/components/orders/creation/OrderCreationSelfServiceStep.vue';
import FinalReviewStep from '@/components/orders/creation/OrderCreationFinalReviewStep.vue';
import { useImportOrderCreationStore } from '@/stores/importOrderCreation';
import PublicationStateIndicator from '@/components/orders/creation/subcomponents/PublicationStateIndicator.vue';
import { useActiveBudgetStore } from '@/stores/activeBudget';
import { useProviderStore } from '@/stores/providers';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { toAutopilotOrder } from '@/autopilot/functions/orders/orderContext';
import { bookingWorkflowGuide } from '@/autopilot/functions/orders/bookingWorkflow';

const listBreadcrumbs = ref([
  { label: translator.get('nav.orders'), path: { name: 'orders.list', query: {} } },
  { label: translator.get('orders.create'), path: { name: 'order.create', query: {} } },
]);

const route = useRoute();
const router = useRouter();

const loadExistingOrderId = route.query.orderId as string | null;
const isReady = ref(true); // controls the step-level loader overlay for steps that require awaiting
const publicationTemplateStepRef = ref<InstanceType<typeof PublicationTemplateStep> | null>(null);
const publicationEditorStepRef = ref<InstanceType<typeof PublicationEditorStep> | null>(null);

/* ---------------------------------- STORE --------------------------------- */
const featureStore = useCustomerFeaturesStore();
const activeBudgetStore = useActiveBudgetStore();
const orderCreationStore = useOrderCreationStore();
const providerStore = useProviderStore();
const {
  order,
  orderDetailsStep,
  isLoading,
  isPublicationLoading,
  isCampaignGenerationLoading,
  productsToSave,
  currentStepSequence,
  openCart,
  availableSteps,
  currentStep,
  canClickNext,
  expectedCampaignProviders,
  generatedCampaigns,
  campaignGenerationTask,
  campaignGenerationPhase,
  skipDetailsStep,
  visibleSteps,
} = storeToRefs(orderCreationStore);

const { allProvidersCardsCompleted } = storeToRefs(providerStore);

const importOrderCreationStore = useImportOrderCreationStore();

/* -------------------------------- AUTOPILOT ------------------------------- */
const routeReady = useAutopilotRouteReady();
useAutopilotContext('booking_workflow', () => bookingWorkflowGuide);
useAutopilotContext('current_order', () => toAutopilotOrder(order.value));
useAutopilotContext('booking_wizard', () => ({
  current_step: currentStep.value?.key ?? null,
  visible_steps: (visibleSteps.value || []).map((step) => step.key),
  can_go_next: canClickNext.value,
}));

activeBudgetStore.getActiveBudget().catch((error) => {
  console.error('Error loading active budget:', error);
});

/* -------------------------------- COMPUTED -------------------------------- */
const pageTitle = computed(() => {
  if (currentStep.value?.key === 'self_service' && order.value?.title) {
    return order.value.title;
  }
  if (loadExistingOrderId) return translator.get('orders.create.title_modify');
  return translator.get('orders.create.title_create');
});
const selfServiceEnabled = computed(() => featureStore.featureOrderManagment?.self_service === true);
// the banner stage runs first and can take minutes on its own, so it gets its own messages
const campaignLoaderMessages = computed(() => {
  const group = campaignGenerationPhase.value === 'banners' ? 'banners' : 'campaigns';
  const count = group === 'banners' ? 4 : 7;
  return Array.from({ length: count }, (_, i) => ({
    title: translator.get(`order_creation.loader.${group}.${i + 1}.title`),
    subtitle: translator.get(`order_creation.loader.${group}.${i + 1}.subtitle`),
  }));
});

// the icon strip walks the whole pipeline: banners first, then one icon per campaign provider
const campaignLoaderStages = computed(() => {
  const generated = new Set(generatedCampaigns.value.map((c: any) => c.provider));
  const inBannerPhase = campaignGenerationPhase.value === 'banners';
  // 'pending' rather than strictly 'in_progress': a task queued behind banner generation sits on
  // 'waiting' for minutes, and the next provider should still be shown as the one being worked on
  const taskRunning = resolveCampaignTaskState(campaignGenerationTask.value) === 'pending';
  const firstPending = expectedCampaignProviders.value.find((p) => !generated.has(p));

  const bannerStage = {
    key: 'banners',
    icon: IconPhoto,
    color: '#7c3aed',
    label: translator.get('order_creation.loader.stage.banners'),
    generated: !inBannerPhase,
    active: inBannerPhase,
  };

  const providerStages = expectedCampaignProviders.value.map((provider) => {
    const meta = getCampaignProviderMeta(provider);
    return {
      key: provider,
      icon: meta.icon,
      color: meta.color,
      label: meta.label,
      generated: generated.has(provider),
      // no provider is being worked on while the banners are still rendering
      active: !inBannerPhase && taskRunning && provider === firstPending,
    };
  });

  return [bannerStage, ...providerStages];
});

const selfServiceStepRef = ref<any>(null);
const mediaStepRef = ref<any>(null);

// on the last self service tab the Continue button can be disabled by an invalid provider the user
// isn't looking at, so explain why they're stuck and where to go from here
const showSelfServiceBlockedHint = computed(
  () =>
    currentStep.value?.key === 'self_service' &&
    !selfServiceStepRef.value?.hasNextProvider &&
    !availableSteps.value.self_service.canGoNext &&
    !isLoading.value
);

// when every card of every provider is saved and the order still can't be continued there is
// nothing left for the user to fix, so point them to support instead of back to the forms
const selfServiceBlockedMessage = computed(() =>
  allProvidersCardsCompleted.value
    ? translator.get('campaigns.self_service.continue_blocked_contact_support')
    : translator.get('campaigns.self_service.continue_blocked_incomplete')
);

/* -------------------------------- LIFECYCLE -------------------------------- */
// initialize the order creation
orderCreationStore.resetState();
importOrderCreationStore.resetState();
onMounted(() => {
  processExternalMarket();
  window.addEventListener('keydown', handleEnterKey);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEnterKey);
  activeBudgetStore.clearActiveBudgetState();
  // generation polling can outlive the page now that resuming a draft starts it on load
  orderCreationStore.stopCampaignGenerationPolling();
});

/* --------------------------------- METHODS -------------------------------- */
function setCorrectStep() {
  // These skip props come from the route query parameters
  // Also check if import type is 'custom' to skip import step
  setWizardStepConfig(order.value.creation_source);

  if (route.query.step) {
    const stepResolution = orderCreationStore.restoreStepFromQuery(route.query.step as string);
    if (!stepResolution.isKnown) {
      console.warn(
        `Attempted to load unknown step ${stepResolution.requestedStep} from query parameter. Clearing step query and defaulting to details.`
      );
      currentStepSequence.value = orderDetailsStep.value.sequence;
      return;
    }
    if (!stepResolution.isVisible) {
      // Route params points to a step the user no longer has access to after a refresh.
      // In that case we send them to a fallback step (first available step after details)
      console.warn(
        `Attempted to load step ${stepResolution.requestedStep} from query parameter, but it's not visible/available. Falling back to ${stepResolution.fallbackStep?.key ?? 'details'}`
      );
      currentStepSequence.value = stepResolution.fallbackStep?.sequence ?? orderDetailsStep.value.sequence;
      return;
    }
    if (stepResolution.canRestore && loadExistingOrderId) {
      return;
    } else router.replace({ query: { ...route.query, orderId: undefined, step: undefined } });
  } else router.replace({ query: { ...route.query, orderId: undefined, pubkey: undefined } });
}
async function handleNextStep() {
  if (
    currentStep.value?.key === 'self_service' &&
    selfServiceStepRef.value &&
    typeof selfServiceStepRef.value.triggerNextProvider === 'function'
  ) {
    const advanced = selfServiceStepRef.value.triggerNextProvider();
    if (advanced) {
      return;
    }
  }

  if (currentStep.value?.key === 'media' && mediaStepRef.value?.saveMediaImages) {
    isLoading.value = true;
    try {
      const saved = await mediaStepRef.value.saveMediaImages();
      if (!saved) return;
    } finally {
      // Add a slight visual delay to ensure the UI updates properly and to avoid flickering in case of fast saves
      setTimeout(() => (isLoading.value = false), 500);
    }
  }

  if (currentStep.value?.key === 'publication_template') {
    const saved = await publicationTemplateStepRef.value?.ensurePublicationSaved();
    if (!saved) return;
  }

  // leaving the options editor: offer to persist the adjustments the user just set
  if (currentStep.value?.key === 'publication_editor' && publicationEditorStepRef.value) {
    const proceed = await publicationEditorStepRef.value.confirmAdjustmentsBeforeLeave();
    if (proceed === false) return;
  }

  if (currentStep.value?.section.onContinue) currentStep.value.section.onContinue();
  else {
    order.value.step = currentStep.value.key;
    if (currentStepSequence.value < orderDetailsStep.value.sequence) return currentStepSequence.value++;

    orderCreationStore
      .updateDraft()
      .then(() => {
        // After updating draft on the products step, poll for campaign generation only if self-service is enabled
        if (selfServiceEnabled.value && currentStep.value.key === 'products' && order.value.id) {
          orderCreationStore.pollCampaignGenerationStatus(order.value.id, {
            onComplete: () => {
              currentStepSequence.value++;
            },
            onError: (err) => {
              console.error('Campaign generation failed:', err);
              // Still advance so the user isn't stuck
              currentStepSequence.value++;
            },
          });
        } else {
          currentStepSequence.value++;
        }
      })
      .catch((err) => {
        if (err?.responseJson?.errors) orderCreationStore.stepErrors = err.responseJson.errors;
        console.error('Error updating order draft:', err);
      });
  }
}

function processExternalMarket() {
  const isExternalOrder = isExternalRouteQueryEnabled();

  if (!isExternalOrder) {
    skipDetailsStep.value = false;
    setWizardStepConfig(order.value.creation_source);
    return;
  }

  const { ejm_job_id, url } = route.query as { ejm_job_id: string; url: string };

  // handle external job market parameters
  // store the ejm_job_id in the order for later use
  order.value.ejm_job_id = parseInt(ejm_job_id, 10);
  order.value.directlink = url;
  order.value.creation_source = 'direct_url';

  // try to pre-populate order data from the current external job market selection
  skipDetailsStep.value = false;
  try {
    // check if we have job data from the external job market store/session
    const selectedJob = sessionStorage.getItem('ejm_selected_job');

    if (selectedJob) {
      const jobData = JSON.parse(selectedJob);

      if (jobData.id == ejm_job_id) {
        const normalizedTitle = (jobData.title || '').toString().trim();
        const normalizedCompany = (jobData.company || jobData.company_name || '').toString().trim();
        const normalizedWorkplaceLocation = (jobData.workplace_location || jobData.workplace_city || '')
          .toString()
          .trim();

        skipDetailsStep.value = !!(normalizedTitle && normalizedCompany && normalizedWorkplaceLocation);

        // pre-populate order fields
        order.value.title = normalizedTitle;
        order.value.company_name = normalizedCompany;
        order.value.workplace_location = normalizedWorkplaceLocation;
        order.value.language = jobData.language;

        // set workload if available
        order.value.workload_min = jobData.workload_min || jobData.workload_max || 100;
        order.value.workload_max = jobData.workload_max || 100;

        // set additional metadata from the external job
        if (jobData.workplace_country) order.value.workplace_country = jobData.workplace_country;
        if (jobData.workplace_zip) order.value.workplace_zip = jobData.workplace_zip;
        if (jobData.workplace_address) order.value.workplace_address = jobData.workplace_address;

        // Set apply URL if available from job properties or use the job link
        if (jobData.apply_url) order.value.apply_url = jobData.apply_url;

        // clean up the session storage after using the data
        sessionStorage.removeItem('ejm_selected_job');
      } else {
        // ID mismatch, do not pre-populate
      }
    }
  } catch {
    // failure to pre-populate
    console.error('Failed to parse external job data for pre-population');
  }

  setWizardStepConfig(order.value.creation_source);
}

function isExternalRouteQueryEnabled() {
  const { ejm_job_id, url } = route.query as { ejm_job_id?: string; url?: string };
  return !!(ejm_job_id && url);
}

function setWizardStepConfig(newImportType: string) {
  const isExternalOrder = isExternalRouteQueryEnabled();
  const shouldSkipUrl = isExternalOrder || route.query.skipUrlStep != null;
  const shouldSkipImport = newImportType === 'custom' || route.query.skipAnalysisStep != null;
  const shouldSkipDetails = isExternalOrder && skipDetailsStep.value;
  const shouldSkipMedia = isExternalOrder && skipDetailsStep.value;

  orderCreationStore.configureWizardSteps(
    shouldSkipUrl,
    isExternalOrder || shouldSkipImport,
    selfServiceEnabled.value,
    shouldSkipDetails,
    shouldSkipMedia
  );
  orderCreationStore.configurePublicationSteps(newImportType === 'direct_url', selfServiceEnabled.value);
}

async function initializeExistingOrder() {
  if (!loadExistingOrderId || !featureStore.hasLoaded) return;
  if (!featureStore.featureOrderManagment) return;

  isLoading.value = true;
  try {
    await orderCreationStore.getOrderById(loadExistingOrderId);
    if (!order.value.is_draft) {
      router.replace({ name: 'order.detail.info', params: { id: loadExistingOrderId } });
      return;
    }

    try {
      await orderCreationStore.getOrderProducts();
      await activeBudgetStore.syncReservedVBudgetsFromProducts(orderCreationStore.productsToSave);
    } catch (error) {
      console.error('Failed to load order products for existing draft', error);
    }
    setCorrectStep();

    // this tab has no generation state of its own, so ask the server whether the draft is still
    // generating its campaigns. it raises the loader before its first await, so the self-service
    // step never renders between here and the answer — keep it right after setCorrectStep().
    if (currentStep.value?.key === 'self_service' && order.value.id) {
      await orderCreationStore.resumeCampaignGenerationIfPending(order.value.id, {
        onSettled: () => providerStore.clearProviders(),
      });
    }

    if (route.query.pubkey) {
      try {
        await orderCreationStore.getPublicationByKey(route.query.pubkey as string);
        orderCreationStore.fetchPublicationTemplates();
      } catch (error) {
        currentStepSequence.value = orderDetailsStep.value.sequence;
        router.replace({ query: { ...route.query, pubkey: undefined } });
        console.error('Failed to load publication with key', route.query.pubkey, error);
      }
    }
  } catch (error: any) {
    const status = error?.responseJson?.status ?? error?.response?.status;
    if ([401, 403, 404].includes(status)) {
      router.replace({ name: 'orders.list', query: {} });
      console.error('Failed to load order with id', loadExistingOrderId, error);
      return;
    }
    console.error('Existing order initialization failed without redirect', loadExistingOrderId, error);
  } finally {
    setTimeout(() => (isLoading.value = false), 1000);
  }
}

function handleEnterKey(event: KeyboardEvent) {
  if (event.key !== 'Enter') return;

  const target = event.target as HTMLElement | null;
  const isMultilineInput =
    target instanceof HTMLTextAreaElement ||
    !!target?.closest('textarea, [contenteditable="true"], [role="textbox"][aria-multiline="true"]');

  // allow Enter to create a new line in textarea-style controls
  if (isMultilineInput) return;

  // Don't trigger on final review or during loading states
  if (['final_review'].includes(currentStep.value.key)) return;

  // Let the cart drawer handle Enter when it's open
  if (openCart.value) return;
  if (isLoading.value || isPublicationLoading.value || isCampaignGenerationLoading.value) return;

  // Don't trigger wizard navigation when a FormCard is open
  if (document.querySelector('.form-card.is-open')) return;

  if (currentStep.value.key === 'self_service') {
    // Mirror the active button's disabled state
    if (selfServiceStepRef.value?.hasNextProvider) {
      // Save & Continue: gated by currentProviderValid
      if (!selfServiceStepRef.value?.currentProviderValid) return;
    } else {
      // Continue: gated by all providers valid
      if (!availableSteps.value.self_service.canGoNext) return;
    }
  } else {
    if (!canClickNext.value) return;
  }

  if (event.defaultPrevented) return;

  event.preventDefault();
  handleNextStep();
}

/* ---------------------------------- WATCH --------------------------------- */
// Watch for prop changes and reconfigure steps
watch([() => route.query.skipUrlStep, () => route.query.skipAnalysisStep], () => {
  processExternalMarket();
});

// Watch for import type changes and skip import step when custom
watch(
  () => order.value.creation_source,
  (newImportType) => {
    setWizardStepConfig(newImportType);
  }
);

watch(
  () => featureStore.hasLoaded,
  async (hasLoaded) => {
    if (!hasLoaded) return;
    setWizardStepConfig(order.value.creation_source);
    await initializeExistingOrder();
    // let the active v-if step mount and claim before we drop the page-level hold
    await nextTick();
    routeReady();
  },
  { immediate: true }
);

watch(currentStepSequence, (newStep) => {
  if (newStep > orderDetailsStep.value.sequence) {
    // Don't delete details:{param} step from the URL
    if (!route.query.step?.includes(currentStep.value.key))
      router.replace({ query: { ...route.query, step: currentStep.value.key } });
    // Update breadcrumbs query to reflect current step
    listBreadcrumbs.value[1].path.query = route.query;
  }
});

// shouldBeAwaited steps start with isReady=false and must emit @ready to clear it
watch(
  () => currentStep.value?.key,
  () => {
    isReady.value = !currentStep.value?.section?.shouldBeAwaited;
  },
  { immediate: true }
);
</script>
<style scoped>
:deep(p),
:deep(label),
:deep(a) {
  font-size: 13px;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(20px) scale(0.98);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-20px) scale(0.98);
}

.position-relative {
  position: relative;
}

/* pint the container so the loader doesn't 'jump' */
.container-lead.pin-to-viewport {
  flex-grow: 1;
  min-height: 0;
}

/* fixed to the viewport (not the container) so header-height changes during load
   don't shift the loader vertically. Left inset matches the dashboard sidebar width. */
.step-loader-overlay {
  position: fixed;
  inset: 0;
  z-index: 10;
  background-color: var(--tblr-body-bg, #fff);
}

@media (min-width: 768px) {
  .step-loader-overlay {
    left: 320px;
  }
}

.wizard-area-fade-enter-active,
.wizard-area-fade-leave-active {
  transition: opacity 0.25s ease;
}

.wizard-area-fade-enter-from,
.wizard-area-fade-leave-to {
  opacity: 0;
}

.step-content-fade {
  transition: opacity 0.3s ease;
  /* clip (not hidden) so page-level vertical scroll still works — `hidden` would force overflow-y: auto */
  overflow-x: clip;
}

/* hide the content while loading so it doesn't interact with the content */
.step-content-hidden {
  opacity: 0;
  pointer-events: none;
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.header-subtitle {
  font-size: 14px;
  color: #475467;
  font-weight: 400;
}

.self-service-blocked-hint {
  max-width: 520px;
  color: #475467;
}

.self-service-blocked-hint p {
  font-size: 12px;
  line-height: 1.35;
}
</style>
